import { useEffect, useMemo, useRef, useState } from "react";
import type { DragEvent, FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminTopline } from "../../components/console";
import { Segmented } from "../../components/dashboard";
import {
  ChevronDown, ChevronUp, ImagePlus, Megaphone, PenLine, Plus, Smartphone, Trash2, X,
} from "../../components/icons";
import { API_BASE_URL, api, authHeaders, describeError } from "../../lib/api";
import { getCurrentRole } from "../../lib/auth";
import { ROLE_LABEL_LO } from "../../lib/roles";

/**
 * ຂ່າວ & ແຈ້ງການ ที่ขึ้นสไลด์บนหน้าหลักของแอปมือถือ (super admin เท่านั้น)
 *
 * ประกาศระดับประเทศ — ทุกคนที่เปิดแอปเห็นเหมือนกัน รวมผู้ที่ยังไม่ได้เข้าสู่ระบบ
 * (ADR: ดู domain.Announcement) จอนี้จึงไม่มีตัวเลือกขอบเขตพื้นที่
 *
 * จอแบ่งเป็น รายการ (ซ้าย) กับ ตัวอย่างในแอป (ขวา) — ผู้ดูแลเห็นทันทีว่าสไลด์ที่เปิดอยู่
 * เรียงยังไงบนมือถือ ลำดับจัดด้วยลูกศรขึ้น/ลง แทนการพิมพ์ตัวเลข (ตัวเลขซ้ำกันได้จนเดาลำดับไม่ออก)
 *
 * รูปอัปแยกจากข้อความเพราะฝั่ง API รับรูปเป็น raw body ไม่ใช่ multipart
 */

type Announcement = {
  id: string;
  title_lo: string;
  body_lo: string;
  image_url: string;
  is_active: boolean;
  sort_order: number;
  version: number;
  created_at: string;
};

type Draft = { title_lo: string; body_lo: string; is_active: boolean };

const TITLE_MAX = 120;
const BODY_MAX = 4000;
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * รูปส่งเป็น raw body — openapi-fetch ส่ง binary ไม่ได้ จึงใช้ fetch ตรง
 *
 * ต้องต่อ API_BASE_URL เองเหมือน client ตัวอื่น ไม่ใช่ path เปล่า ๆ — deployment ที่
 * ตั้ง VITE_API_BASE_URL (API คนละ origin) จะยิงผิดที่และได้ 404 ของ web server แทน
 */
async function putImage(id: string, file: File): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/v1/admin/announcements/${id}/image`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": file.type },
    body: file,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `ອັບໂຫຼດຮູບບໍ່ສຳເລັດ (${String(res.status)})`);
  }
}

async function putAnnouncement(a: Announcement, patch: Partial<Draft & { sort_order: number }>) {
  const res = await api.PUT("/v1/admin/announcements/{id}", {
    headers: authHeaders(),
    params: { path: { id: a.id } },
    body: {
      title_lo: a.title_lo,
      body_lo: a.body_lo,
      is_active: a.is_active,
      sort_order: a.sort_order,
      ...patch,
      version: a.version,
    },
  });
  if (res.error) throw new Error(describeError(res.error, res.response.status));
}

/** ตรวจไฟล์ก่อนส่ง — บอกผู้ใช้ทันที ไม่ต้องรอ server ปฏิเสธ */
function imageProblem(file: File): string | null {
  if (!IMAGE_TYPES.includes(file.type)) return "ຮອງຮັບສະເພາະ JPEG, PNG ຫຼື WebP";
  if (file.size > IMAGE_MAX_BYTES) return "ຮູບໃຫຍ່ເກີນ 5 MB";
  return null;
}

/** ลำดับที่แสดง = ลำดับเดียวกับที่แอปเรียง (sort_order แล้วค่อยเวลาสร้าง) */
function ordered(items: Announcement[]): Announcement[] {
  return [...items].sort((a, b) => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at));
}

type Filter = "all" | "active" | "hidden";

export function AnnouncementsPage() {
  const qc = useQueryClient();
  const role = getCurrentRole();
  const [editor, setEditor] = useState<{ item: Announcement | null } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Announcement | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  const list = useQuery({
    queryKey: ["announcements"],
    queryFn: async () => {
      const res = await api.GET("/v1/admin/announcements", { headers: authHeaders(), cache: "no-store" });
      if (res.error || !res.data) throw new Error(describeError(res.error, res.response.status));
      return ordered(res.data.items as Announcement[]);
    },
  });
  const items = list.data ?? [];
  const activeItems = items.filter((a) => a.is_active);
  const shown = items.filter((a) => (filter === "all" ? true : filter === "active" ? a.is_active : !a.is_active));

  function flash(text: string) {
    setToast(text);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  }
  const refresh = () => qc.invalidateQueries({ queryKey: ["announcements"] });

  const toggle = useMutation({
    mutationFn: (a: Announcement) => putAnnouncement(a, { is_active: !a.is_active }),
    onSuccess: async (_d, a) => {
      await refresh();
      flash(a.is_active ? "ປິດການສະແດງແລ້ວ" : "ເປີດສະແດງໃນແອັບແລ້ວ");
    },
    onError: (e: Error) => setError(e.message),
  });

  // ย้ายลำดับ = เรียงใหม่ทั้งชุดเป็น 0..n-1 แล้วบันทึกเฉพาะใบที่ค่าเปลี่ยน
  // (ค่าเดิมอาจซ้ำกัน การสลับแค่สองใบจึงไม่พอให้ลำดับนิ่ง)
  const move = useMutation({
    mutationFn: async ({ id, dir }: { id: string; dir: -1 | 1 }) => {
      const next = [...items];
      const i = next.findIndex((a) => a.id === id);
      const j = i + dir;
      const a = next[i];
      const b = next[j];
      if (!a || !b) return;
      next[i] = b;
      next[j] = a;
      for (const [k, item] of next.entries()) {
        if (item.sort_order !== k) await putAnnouncement(item, { sort_order: k });
      }
    },
    onSuccess: () => void refresh(),
    onError: (e: Error) => {
      setError(e.message);
      void refresh();
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE("/v1/admin/announcements/{id}", {
        headers: authHeaders(),
        params: { path: { id } },
      });
      if (res.error) throw new Error(describeError(res.error, res.response.status));
    },
    onSuccess: async () => {
      setConfirmDelete(null);
      await refresh();
      flash("ລຶບແຈ້ງການແລ້ວ");
    },
    onError: (e: Error) => setError(e.message),
  });

  const busy = toggle.isPending || move.isPending;
  const nextOrder = items.length === 0 ? 0 : Math.max(...items.map((a) => a.sort_order)) + 1;

  return (
    <>
      <AdminTopline
        title="ຂ່າວ & ແຈ້ງການ"
        subtitle="ສະແດງເປັນສະໄລ້ຢູ່ໜ້າຫຼັກຂອງແອັບ — ທຸກຄົນເຫັນຄືກັນ ລວມທັງຜູ້ທີ່ຍັງບໍ່ໄດ້ເຂົ້າສູ່ລະບົບ"
        roleLabel={role ? ROLE_LABEL_LO[role] : undefined}
        actions={
          <button type="button" className="btn-primary" onClick={() => setEditor({ item: null })}>
            <Plus size={16} /> ເພີ່ມແຈ້ງການ
          </button>
        }
      />

      {error ? (
        <div className="alert-error ann-alert">
          <span>{error}</span>
          <button type="button" className="ann-icon-btn" aria-label="ປິດ" onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      ) : null}

      <div className="ann-layout">
        <section className="panel ann-list-panel">
          <div className="ann-list-head">
            <div>
              <h2 className="panel__title" style={{ marginBottom: 0 }}>
                ລາຍການແຈ້ງການ
              </h2>
              <p className="caption">
                ເປີດຢູ່ <span className="num">{activeItems.length}</span> ຈາກ <span className="num">{items.length}</span>{" "}
                ລາຍການ · ລຽງຕາມລຳດັບທີ່ສະແດງໃນແອັບ
              </p>
            </div>
            <Segmented
              label="ກັ່ນຕອງ"
              value={filter}
              onChange={setFilter}
              options={[
                { value: "all", label: "ທັງໝົດ" },
                { value: "active", label: "ເປີດຢູ່" },
                { value: "hidden", label: "ປິດຢູ່" },
              ]}
            />
          </div>

          {list.isPending ? (
            <div className="ann-empty">ກຳລັງໂຫຼດ…</div>
          ) : list.error ? (
            <div className="alert-error">{list.error.message}</div>
          ) : items.length === 0 ? (
            <div className="ann-empty">
              <span className="ann-empty__icon">
                <Megaphone size={22} />
              </span>
              <strong>ຍັງບໍ່ມີແຈ້ງການ</strong>
              <span className="caption">ແຈ້ງການທີ່ເປີດໄວ້ຈະຂຶ້ນເປັນສະໄລ້ຢູ່ໜ້າຫຼັກຂອງແອັບ</span>
              <button type="button" className="btn-secondary" onClick={() => setEditor({ item: null })}>
                <Plus size={16} /> ເພີ່ມອັນທຳອິດ
              </button>
            </div>
          ) : shown.length === 0 ? (
            <div className="ann-empty">ບໍ່ມີລາຍການໃນກຸ່ມນີ້</div>
          ) : (
            <ol className="ann-list">
              {shown.map((a) => {
                const idx = items.findIndex((x) => x.id === a.id);
                return (
                  <li key={a.id} className={a.is_active ? "ann-item" : "ann-item is-hidden"}>
                    <div className="ann-item__order">
                      <button
                        type="button"
                        className="ann-icon-btn"
                        aria-label="ຍ້າຍຂຶ້ນ"
                        disabled={busy || idx === 0 || filter !== "all"}
                        onClick={() => move.mutate({ id: a.id, dir: -1 })}
                      >
                        <ChevronUp size={16} />
                      </button>
                      <span className="num">{idx + 1}</span>
                      <button
                        type="button"
                        className="ann-icon-btn"
                        aria-label="ຍ້າຍລົງ"
                        disabled={busy || idx === items.length - 1 || filter !== "all"}
                        onClick={() => move.mutate({ id: a.id, dir: 1 })}
                      >
                        <ChevronDown size={16} />
                      </button>
                    </div>

                    <button type="button" className="ann-item__thumb" onClick={() => setEditor({ item: a })} aria-label="ແກ້ໄຂ">
                      {a.image_url ? (
                        <img src={a.image_url} alt="" />
                      ) : (
                        <span className="ann-item__noimg">
                          <ImagePlus size={18} />
                          ຍັງບໍ່ມີຮູບ
                        </span>
                      )}
                    </button>

                    <div className="ann-item__body">
                      <div className="ann-item__title">{a.title_lo}</div>
                      <p className="ann-item__text">
                        {a.body_lo ? a.body_lo : <span className="ann-item__muted">ບໍ່ມີລາຍລະອຽດ — ກົດສະໄລ້ບໍ່ໄດ້</span>}
                      </p>
                    </div>

                    <div className="ann-item__side">
                      <label className="switch" title={a.is_active ? "ກົດເພື່ອປິດ" : "ກົດເພື່ອເປີດ"}>
                        <input
                          type="checkbox"
                          checked={a.is_active}
                          disabled={busy}
                          onChange={() => toggle.mutate(a)}
                        />
                        <span className="switch__track" aria-hidden="true" />
                        <span className="switch__label">{a.is_active ? "ເປີດຢູ່" : "ປິດຢູ່"}</span>
                      </label>
                      <div className="ann-item__actions">
                        <button type="button" className="btn-outline btn-sm" onClick={() => setEditor({ item: a })}>
                          <PenLine size={14} /> ແກ້ໄຂ
                        </button>
                        <button
                          type="button"
                          className="ann-icon-btn ann-icon-btn--danger"
                          aria-label="ລຶບ"
                          onClick={() => setConfirmDelete(a)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
          {filter !== "all" && shown.length > 0 ? (
            <p className="panel__note">ຍ້າຍລຳດັບໄດ້ສະເພາະຕອນເບິ່ງ "ທັງໝົດ"</p>
          ) : null}
        </section>

        <aside className="panel ann-preview">
          <h2 className="panel__title">
            <Smartphone size={15} /> ຕົວຢ່າງໃນແອັບ
          </h2>
          <PhonePreview items={activeItems} />
          <p className="panel__note">ສະແດງສະເພາະແຈ້ງການທີ່ເປີດຢູ່ ຕາມລຳດັບ · ໃນແອັບເລື່ອນເອງທຸກ 5 ວິນາທີ</p>
        </aside>
      </div>

      {editor ? (
        <EditorModal
          item={editor.item}
          nextOrder={nextOrder}
          onClose={() => setEditor(null)}
          onSaved={async (msg) => {
            setEditor(null);
            await refresh();
            flash(msg);
          }}
        />
      ) : null}

      {confirmDelete ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ລຶບແຈ້ງການ">
          <div className="modal modal--sm">
            <div className="modal__header">
              <h2 className="modal__title">ລຶບແຈ້ງການນີ້?</h2>
            </div>
            <p className="body-sm" style={{ marginBottom: "var(--space-6)" }}>
              <strong>{confirmDelete.title_lo}</strong> ຈະຫາຍໄປຈາກແອັບທັນທີ ແລະ ກູ້ຄືນຈາກໜ້ານີ້ບໍ່ໄດ້ — ຖ້າພຽງແຕ່ຢາກເຊື່ອງໄວ້ກ່ອນ
              ໃຫ້ປິດການສະແດງແທນ
            </p>
            <div className="modal__actions">
              <button type="button" className="btn-secondary" onClick={() => setConfirmDelete(null)}>
                ຍົກເລີກ
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate(confirmDelete.id)}
              >
                ລຶບ
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="ann-toast" role="status">
          {toast}
        </div>
      ) : null}
    </>
  );
}

/** หน้าตาเหมือน AnnouncementSlider ของแอป (รูป + ไล่เงาดำ + หัวข้อสีขาว) */
function SlideCard({ title, image }: { title: string; image: string }) {
  return (
    <div className="ann-slide">
      {image ? <img src={image} alt="" /> : null}
      <span className="ann-slide__shade" />
      <span className="ann-slide__title">{title || "ຫົວຂໍ້ແຈ້ງການ"}</span>
    </div>
  );
}

function PhonePreview({ items }: { items: Announcement[] }) {
  const [page, setPage] = useState(0);
  const current = items.length > 0 ? Math.min(page, items.length - 1) : 0;
  useEffect(() => {
    if (items.length < 2) return;
    const t = window.setInterval(() => setPage((p) => (p + 1) % items.length), 5000);
    return () => window.clearInterval(t);
  }, [items.length]);

  return (
    <div className="ann-phone">
      <div className="ann-phone__screen">
        <div className="ann-phone__section">ຂ່າວ & ແຈ້ງການ</div>
        {items.length === 0 ? (
          <div className="ann-phone__empty">ບໍ່ມີແຈ້ງການທີ່ເປີດຢູ່ — ສ່ວນນີ້ຈະບໍ່ສະແດງໃນແອັບ</div>
        ) : (
          <>
            <div className="ann-phone__viewport">
              <div className="ann-phone__track" style={{ transform: `translateX(-${String(current * 100)}%)` }}>
                {items.map((a) => (
                  <div key={a.id} className="ann-phone__page">
                    <SlideCard title={a.title_lo} image={a.image_url} />
                  </div>
                ))}
              </div>
            </div>
            {items.length > 1 ? (
              <div className="ann-phone__dots">
                {items.map((a, i) => (
                  <button
                    key={a.id}
                    type="button"
                    aria-label={`ສະໄລ້ທີ ${String(i + 1)}`}
                    className={i === current ? "is-active" : undefined}
                    onClick={() => setPage(i)}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
        <div className="ann-phone__ghost" />
        <div className="ann-phone__ghost ann-phone__ghost--short" />
      </div>
    </div>
  );
}

function EditorModal({
  item,
  nextOrder,
  onClose,
  onSaved,
}: {
  item: Announcement | null;
  nextOrder: number;
  onClose: () => void;
  onSaved: (msg: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState<Draft>(
    item ? { title_lo: item.title_lo, body_lo: item.body_lo, is_active: item.is_active } : { title_lo: "", body_lo: "", is_active: true },
  );
  /// รูปที่เลือกไว้ — อัปหลังบันทึกข้อความสำเร็จ (API รับรูปคนละ request)
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  // รูปที่โชว์: ไฟล์ที่เพิ่งเลือก ถ้าไม่มีก็รูปเดิมของใบที่กำลังแก้
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : (item?.image_url ?? "")), [file, item]);
  // blob URL ต้องคืนทิ้งเอง ไม่งั้นรั่วทุกครั้งที่เลือกไฟล์ใหม่
  useEffect(() => {
    if (!file) return;
    return () => URL.revokeObjectURL(preview);
  }, [file, preview]);

  function pick(f: File | undefined) {
    if (!f) return;
    const problem = imageProblem(f);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setFile(f);
  }

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...draft, sort_order: item ? item.sort_order : nextOrder, version: item?.version ?? 0 };
      const res = item
        ? await api.PUT("/v1/admin/announcements/{id}", { headers: authHeaders(), params: { path: { id: item.id } }, body })
        : await api.POST("/v1/admin/announcements", { headers: authHeaders(), body });
      if (res.error || !res.data) throw new Error(describeError(res.error, res.response.status));
      // ข้อความบันทึกแล้วแต่รูปล้ม = ใบนี้มีอยู่จริงแล้ว บอกให้ชัดว่าเหลือแค่รูป
      if (file) {
        try {
          await putImage(res.data.id, file);
        } catch (e) {
          throw new Error(`ບັນທຶກຂໍ້ຄວາມແລ້ວ ແຕ່ອັບໂຫຼດຮູບບໍ່ສຳເລັດ: ${(e as Error).message}`);
        }
      }
    },
    onSuccess: () => void onSaved(item ? "ບັນທຶກການແກ້ໄຂແລ້ວ" : "ເພີ່ມແຈ້ງການແລ້ວ"),
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!draft.title_lo.trim()) {
      setError("ກະລຸນາໃສ່ຫົວຂໍ້");
      return;
    }
    save.mutate();
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    pick(e.dataTransfer.files[0]);
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={item ? "ແກ້ໄຂແຈ້ງການ" : "ເພີ່ມແຈ້ງການ"}>
      <form className="modal ann-editor" onSubmit={submit}>
        <div className="modal__header">
          <h2 className="modal__title">{item ? "ແກ້ໄຂແຈ້ງການ" : "ເພີ່ມແຈ້ງການ"}</h2>
          <button type="button" className="ann-icon-btn" aria-label="ປິດ" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="ann-editor__grid">
          <div className="stack">
            <div>
              <div className="ann-field-head">
                <label htmlFor="ann-title" className="form-field-label">
                  ຫົວຂໍ້<span className="required"> *</span>
                </label>
                <span className="caption num">
                  {draft.title_lo.length}/{TITLE_MAX}
                </span>
              </div>
              <input
                id="ann-title"
                className="input-field"
                maxLength={TITLE_MAX}
                autoFocus
                placeholder="ເຊັ່ນ: ຂໍເອກະສານຜ່ານແອັບໄດ້ແລ້ວ"
                value={draft.title_lo}
                onChange={(e) => setDraft({ ...draft, title_lo: e.target.value })}
              />
            </div>
            <div>
              <div className="ann-field-head">
                <label htmlFor="ann-body" className="form-field-label">
                  ລາຍລະອຽດ
                </label>
                <span className="caption num">
                  {draft.body_lo.length}/{BODY_MAX}
                </span>
              </div>
              <textarea
                id="ann-body"
                className="input-field"
                rows={6}
                maxLength={BODY_MAX}
                placeholder="ຂໍ້ຄວາມທີ່ຜູ້ໃຊ້ເຫັນເມື່ອກົດສະໄລ້"
                value={draft.body_lo}
                onChange={(e) => setDraft({ ...draft, body_lo: e.target.value })}
              />
              <p className="caption" style={{ marginTop: "var(--space-1)" }}>
                ວ່າງໄວ້ໄດ້ — ຖ້າວ່າງ ສະໄລ້ຈະກົດເບິ່ງລາຍລະອຽດບໍ່ໄດ້
              </p>
            </div>
            <label className="switch switch--row">
              <input
                type="checkbox"
                checked={draft.is_active}
                onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
              />
              <span className="switch__track" aria-hidden="true" />
              <span>
                <span className="switch__label">ເປີດສະແດງໃນແອັບ</span>
                <span className="caption" style={{ display: "block" }}>
                  ປິດໄວ້ = ບັນທຶກໄວ້ກ່ອນ ຍັງບໍ່ໃຫ້ຜູ້ໃຊ້ເຫັນ
                </span>
              </span>
            </label>
          </div>

          <div className="stack">
            <div>
              <span className="form-field-label">ຮູບແບນເນີ</span>
              {/* input[type=file] ดิบหน้าตาไม่เข้ากับ design system — ซ่อนไว้แล้วให้กรอบเป็นตัวเปิดแทน
                  ลากไฟล์มาวางได้ด้วย */}
              <button
                type="button"
                className={dragOver ? "ann-drop is-over" : "ann-drop"}
                onClick={() => picker.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
              >
                {preview ? (
                  <>
                    <img src={preview} alt="" />
                    <span className="ann-drop__overlay">
                      <ImagePlus size={18} /> ປ່ຽນຮູບ
                    </span>
                  </>
                ) : (
                  <span className="ann-drop__empty">
                    <ImagePlus size={22} />
                    <strong>ກົດເພື່ອເລືອກຮູບ ຫຼື ລາກມາວາງ</strong>
                    <span className="caption">JPEG / PNG / WebP ບໍ່ເກີນ 5 MB · ແນະນຳ 16:9 (ເຊັ່ນ 1280×720)</span>
                  </span>
                )}
              </button>
              <input
                ref={picker}
                type="file"
                accept={IMAGE_TYPES.join(",")}
                hidden
                onChange={(e) => {
                  pick(e.target.files?.[0]);
                  e.target.value = ""; // เลือกไฟล์เดิมซ้ำต้องยิง change อีกครั้ง
                }}
              />
              {file ? (
                <p className="caption ann-file">
                  <span>{file.name}</span>
                  <button type="button" className="link-action" onClick={() => setFile(null)}>
                    ເອົາອອກ
                  </button>
                </p>
              ) : null}
            </div>
            <div>
              <span className="form-field-label">ຕົວຢ່າງສະໄລ້</span>
              <SlideCard title={draft.title_lo} image={preview} />
            </div>
          </div>
        </div>

        {error ? (
          <div className="alert-error" style={{ marginTop: "var(--space-4)" }}>
            {error}
          </div>
        ) : null}

        <div className="ann-editor__actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            ຍົກເລີກ
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? "ກຳລັງບັນທຶກ…" : item ? "ບັນທຶກການແກ້ໄຂ" : "ເພີ່ມແຈ້ງການ"}
          </button>
        </div>
      </form>
    </div>
  );
}
